import { useState } from "react";
import { Link } from "react-router-dom";
import { faqs } from "@/mocks/faqs";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="补课答疑 · 常见问题 | 瓜田黑料"
        description="瓜田黑料常见问题汇总：内容是否原创、是否会带主观判断、话题收录标准、TOP榜排序依据、名场面筛选方式与联系方式，一次说清楚。"
        keywords="瓜田黑料常见问题,补课答疑,内容原则,TOP榜排序"
        path="/faq"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://jiemia.cn/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "补课答疑",
                  item: "https://jiemia.cn/faq",
                },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "补课答疑" }]} />
        </div>

        <section className="w-full px-4 md:px-10 pt-6 pb-12">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
              <span className="w-4 h-px bg-primary-400" />
              FAQ
            </span>
            <h1 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
              补课答疑 · 常见问题
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
              关于内容来源、整理方式、榜单排序与联系方式的高频问题都在这里。如果看完还有疑问，欢迎通过关于页面的线索征集渠道联系我们。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
            <div className="space-y-3">
              {faqs.map((item, index) => {
                const isOpen = openIndex === index;
                return (
                  <div
                    key={item.q}
                    className={`rounded-lg border transition-colors ${
                      isOpen
                        ? "bg-background-100 border-primary-300"
                        : "bg-background-50 border-background-200"
                    }`}
                  >
                    <h2>
                      <button
                        type="button"
                        onClick={() => setOpenIndex(isOpen ? null : index)}
                        aria-expanded={isOpen}
                        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 md:px-6 md:py-5"
                      >
                        <span className="font-heading font-bold text-sm md:text-base text-foreground-950">
                          {item.q}
                        </span>
                        <span
                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-lg transition-colors ${
                            isOpen
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          <i className={isOpen ? "ri-subtract-line" : "ri-add-line"} />
                        </span>
                      </button>
                    </h2>
                    {isOpen && (
                      <div className="px-5 md:px-6 pb-5 -mt-1">
                        <p className="text-sm leading-loose text-foreground-600">{item.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                <h2 className="font-heading font-bold text-base text-secondary-900">
                  <i className="ri-customer-service-2-line mr-1.5" />
                  还有别的疑问？
                </h2>
                <p className="mt-2 text-xs leading-relaxed text-secondary-800">
                  可以直接在关于页面提交你的问题，我们会挑选高频的补充到这里。
                </p>
                <Link
                  to="/about"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-secondary-800 hover:text-secondary-600"
                >
                  去提交问题
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <div className="rounded-lg bg-accent-50 border border-accent-200 p-5">
                <h2 className="font-heading font-bold text-base text-accent-900">
                  <i className="ri-lightbulb-line mr-1.5" />
                  补课建议
                </h2>
                <ol className="mt-3 space-y-3">
                  {[
                    { title: "先看年度盘点", desc: "建立整体框架，知道今年都发生过什么。" },
                    { title: "再看名场面", desc: "快速感受氛围，从轻松的入口进入。" },
                    { title: "最后看经典案例", desc: "理解争议背后的规则，而不是只看情绪。" },
                  ].map((step, i) => (
                    <li key={step.title} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-500 text-foreground-950 text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-950">
                          {step.title}
                        </span>
                        <span className="block text-xs text-foreground-600 mt-0.5">{step.desc}</span>
                      </span>
                    </li>
                  ))}
                </ol>
                <Link
                  to="/rankings"
                  className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-accent-900 hover:text-accent-700"
                >
                  从年度盘点开始
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}